import { AnimatePresence, motion, type Transition } from "motion/react";
import React from "react";
import { useReducedMotion } from "../../../hooks/useReducedMotion.hook";
import {
    isUiMotionEnabled,
    resolveUiEaseOut,
} from "../../shared/motion/uiMotion.tools";

interface BikeStationPopupModernMotionShellProps {

    /** Popup card and arrow content. */
    "children": React.ReactNode;

    /** Stable key for AnimatePresence enter/exit when the selected station changes. */
    "presenceKey": string;

    /** Whether the popup should be mounted and visible. */
    "visible": boolean;

}

/**
 * Builds the enter transition for the bike station popup shell.
 *
 * @param reducedMotion Whether the user prefers reduced motion.
 * @returns Motion enter transition.
 */
const buildBikeStationPopupModernEnterTransition = ( reducedMotion: boolean ): Transition => {
    return resolveUiEaseOut( reducedMotion, 180 );
};

/**
 * Builds the dismiss transition for the bike station popup shell.
 *
 * @param reducedMotion Whether the user prefers reduced motion.
 * @returns Motion exit transition.
 */
const buildBikeStationPopupModernExitTransition = ( reducedMotion: boolean ): Transition => {
    return resolveUiEaseOut( reducedMotion, 160 );
};

/**
 * Map-anchored motion shell for the modern bike station popup.
 *
 * Positioning (`left`/`top`) is owned by the parent wrapper so the DOM ref
 * stays stable and does not pass through AnimatePresence ref cloning.
 *
 * Enter/exit uses opacity only (no transform) so touch scrolling works on iOS WebKit.
 */
const BikeStationPopupModernMotionShell: React.FC<BikeStationPopupModernMotionShellProps> = ( {
    children,
    presenceKey,
    visible,
} ) => {
    const reducedMotion = useReducedMotion();
    const animationsEnabled = isUiMotionEnabled( reducedMotion );

    if ( !animationsEnabled ) {
        if ( !visible ) {
            return null;
        }

        return children;
    }

    return (
        <AnimatePresence>
            {visible
                ? (
                    <motion.div
                        animate={{ "opacity": 1 }}
                        exit={{
                            "opacity":    0,
                            "transition": buildBikeStationPopupModernExitTransition( reducedMotion ),
                        }}
                        initial={{ "opacity": 0 }}
                        key={presenceKey}
                        transition={buildBikeStationPopupModernEnterTransition( reducedMotion )}
                    >
                        {children}
                    </motion.div>
                )
                : null}
        </AnimatePresence>
    );
};

export default BikeStationPopupModernMotionShell;
